import { CredentialCreationOptionsJSON, PublicKeyCredentialWithAttestationJSON, create } from '@github/webauthn-json';
import { ObservableMap, action, computed, observable, runInAction } from 'mobx';
import { API, FetchStatus } from '../util/ApiUtil';
import { isTeliaDomain } from '../util/BrowserUtil';
import ErrorUtil from '../util/ErrorUtil';

type PublicKeyCredentialCreationOptionsJSON = CredentialCreationOptionsJSON['publicKey'];

export interface CredentialItem extends CredentialMetaData {
  id: string;
}

export interface CredentialMetaData {
  deviceType: string;
  deviceName: string;
  customName: string | null;
  city: string;
  country: string;
  lastUsed: Date;
  active: boolean;
}

export interface CredentialsResponse {
  credentials: Map<string, CredentialMetaData>;
}

interface CredentialUpdate {
  customName?: string;
}

const REGISTRATION_AUTH_FAILURE_REASONS = ['session.notPersonallyAuthenticated', 'session.notFound', 'session.notHardAuth'];

export default class BiometryStore {
  @observable accessor sortedCredentialList: CredentialItem[] = [];
  @observable accessor credentialInDeletion: string | null = null;
  @observable accessor credentialInEditing: string | null = null;
  @observable accessor customName: string | undefined = '';
  @observable accessor isModalOpenMap: Map<string, boolean> = new Map<string, boolean>();
  @observable accessor registrationFailureReason: string | null = null;

  @observable accessor credentials: Map<string, CredentialMetaData> = new ObservableMap<string, CredentialMetaData>();
  @observable accessor biometryAvailable: boolean = false;
  @observable accessor registrationStatus: FetchStatus = FetchStatus.UNINITIALIZED;
  @observable accessor credentialFetchStatus: FetchStatus = FetchStatus.UNINITIALIZED;
  @observable accessor deletionStatus: FetchStatus = FetchStatus.UNINITIALIZED;
  @observable accessor editStatus: FetchStatus = FetchStatus.UNINITIALIZED;

  @computed
  public get isBiometryAvailable() {
    return this.biometryAvailable;
  }

  @computed
  public get isRegistrationPending() {
    return this.registrationStatus === FetchStatus.FETCHING;
  }

  @computed
  public get isRegistrationFailed() {
    return this.registrationStatus === FetchStatus.ERROR;
  }

  @computed
  public get isRegistrationSuccess() {
    return this.registrationStatus === FetchStatus.FETCHED;
  }

  @computed
  public get isCredentialsFetching() {
    return [FetchStatus.UNINITIALIZED, FetchStatus.FETCHING].includes(this.credentialFetchStatus);
  }

  @computed
  public get isCredentialsFetchFailed() {
    return this.credentialFetchStatus === FetchStatus.ERROR;
  }

  @computed
  public get isDeletionFailed() {
    return this.deletionStatus === FetchStatus.ERROR;
  }

  @computed
  public get isDeletionSuccess() {
    return this.deletionStatus === FetchStatus.FETCHED;
  }

  @computed
  public get isEditPending() {
    return this.editStatus === FetchStatus.FETCHING;
  }

  @computed
  public get isEditSuccess() {
    return this.editStatus === FetchStatus.FETCHED;
  }

  @computed
  public get isEditFailed() {
    return this.editStatus === FetchStatus.ERROR;
  }

  @action.bound
  private setBiometryAvailable(available: boolean) {
    this.biometryAvailable = available;
  }

  public register = async () => {
    runInAction(() => {
      this.deletionStatus = FetchStatus.UNINITIALIZED;
      this.registrationStatus = FetchStatus.FETCHING;
    });
    try {
      const options = await this.initiateRegistration();
      const credential = await create({ publicKey: options });
      const response = await this.submitCredentialForRegistration(credential);
      this.updateCredentialsMap(response);
      runInAction(() => {
        this.registrationStatus = FetchStatus.FETCHED;
      });
    } catch (e: Error | string | any) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      runInAction(() => {
        this.registrationStatus = FetchStatus.ERROR;
        // See https://www.w3.org/TR/webauthn/ CTRL+F "InvalidStateError"
        if (e.name === 'InvalidStateError') {
          this.registrationFailureReason = 'duplicateError';
        } else if (REGISTRATION_AUTH_FAILURE_REASONS.includes(e.response?.data?.error?.code)) {
          this.registrationFailureReason = 'notSufficientAuth';
        } else {
          this.registrationFailureReason = null;
        }
      });
    }
  };

  public initBiometry = async (): Promise<void> => {
    const biometryAvailable = isTeliaDomain() && (await window?.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable());
    this.setBiometryAvailable(biometryAvailable);
  };

  public async loadCredentials() {
    if (!isTeliaDomain()) {
      return;
    }
    try {
      runInAction(() => {
        this.credentialFetchStatus = FetchStatus.FETCHING;
      });
      const response = await this.fetchCredentials();
      this.updateCredentialsMap(response);
      runInAction(() => {
        this.credentialFetchStatus = FetchStatus.FETCHED;
      });
    } catch (e: Error | string | any) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      runInAction(() => {
        this.credentialFetchStatus = FetchStatus.ERROR;
      });
    }
  }

  public removeCredential = async (credentialId: string) => {
    try {
      this.credentialInDeletion = credentialId;
      this.registrationStatus = FetchStatus.UNINITIALIZED;
      this.deletionStatus = FetchStatus.FETCHING;
      await this.deleteCredential(credentialId);
      this.credentials.delete(credentialId);
      this.sortCredentialsIntoList();
      this.deletionStatus = FetchStatus.FETCHED;
    } catch (e: Error | string | any) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      this.deletionStatus = FetchStatus.ERROR;
    }
    this.credentialInDeletion = null;
  };

  public startDeviceRename = (credentialId: string) => {
    if (!this.credentials?.get(credentialId)?.customName) {
      ErrorUtil.pushErrorToTeliaRumAndLogWithLocation('this.credentials?.get(credentialId)?.customName has no value');
      return;
    }
    this.credentialInEditing = credentialId;
    this.customName = this.credentials.get(credentialId)?.customName || undefined;
  };

  public cancelDeviceRename = () => {
    this.credentialInEditing = null;
    this.customName = undefined;
  };

  public updateDeviceName = (value: string) => {
    this.customName = value;
  };

  public saveDeviceName = async (credentialId: string) => {
    if (credentialId === this.credentialInEditing) {
      try {
        runInAction(() => {
          this.editStatus = FetchStatus.FETCHING;
        });
        const response = await this.updateCredential(credentialId, { customName: this.customName });
        this.updateCredentialsMap(response);
        runInAction(() => {
          this.credentialInEditing = null;
          this.customName = undefined;
          this.editStatus = FetchStatus.FETCHED;
        });
      } catch (e: Error | string | any) {
        ErrorUtil.pushErrorToTeliaRumAndLog(e);
        runInAction(() => {
          this.editStatus = FetchStatus.ERROR;
        });
      }
    }
  };

  public handleIsModalOpen = (id: string, isOpen: boolean) => {
    this.isModalOpenMap.set(id, isOpen);
  };

  private sortCredentialsIntoList = () => {
    const list: CredentialItem[] = [];
    this.credentials.forEach((credential, id) => {
      list.push({ ...credential, id });
    });
    list.sort((a, b) => {
      return new Date(b.lastUsed).getTime() - new Date(a.lastUsed).getTime();
    });
    this.sortedCredentialList = list;
  };

  private updateCredentialsMap = (credentialsResponse: CredentialsResponse) => {
    new Map(Object.entries(credentialsResponse.credentials)).forEach((value, key) => {
      this.credentials.set(key, value);
    });
    this.sortCredentialsIntoList();
  };

  private initiateRegistration = async (): Promise<PublicKeyCredentialCreationOptionsJSON> => {
    const response = await API.post('/biometry/registration/initiate');
    return response.data;
  };

  private submitCredentialForRegistration = async (credential: PublicKeyCredentialWithAttestationJSON): Promise<CredentialsResponse> => {
    const response = await API.post('/biometry/registration/complete', {
      id: credential.id,
      response: { ...credential.response },
      clientExtensionResults: credential.clientExtensionResults,
      type: credential.type
    });
    return response.data;
  };

  private fetchCredentials = async (): Promise<CredentialsResponse> => {
    const response = await API.get('/biometry/credentials');
    return response.data;
  };

  private deleteCredential = async (credentialId: string) => {
    await API.delete(`/biometry/credential/${credentialId}`);
  };

  private updateCredential = async (credentialId: string, updateBody: CredentialUpdate): Promise<CredentialsResponse> => {
    const response = await API.put(`/biometry/credential/${credentialId}`, updateBody);
    return response.data;
  };
}
